import React, { useState, useCallback } from "react";
import { X, Upload, CheckCircle, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FileUploader } from "@core/file-uploader";
import { TeacherDocumentResponseDto } from "@packages/api/madlenAPI.schemas";

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: (file: File, response: TeacherDocumentResponseDto) => void;
  onUploadError: (error: string) => void;
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in MB
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  onUploadSuccess,
  onUploadError,
}) => {
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedDocument, setUploadedDocument] = useState<
    TeacherDocumentResponseDto | undefined
  >();

  const handleUploadSuccess = useCallback(
    (file: File, response: TeacherDocumentResponseDto) => {
      setUploadedDocument(response);
      setUploadError(null);
      onUploadSuccess(file, response);
    },
    [onUploadSuccess],
  );

  const handleUploadError = useCallback(
    (error: string) => {
      setUploadError(error);
      onUploadError(error);
    },
    [onUploadError],
  );

  const handleFileDelete = useCallback(() => {
    setUploadedDocument(undefined);
    setUploadError(null);
  }, []);

  const handleClose = useCallback(() => {
    setUploadError(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <Upload className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t("fileUploadModal.title")}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("fileUploadModal.subtitle")}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Upload Content */}
        <div className="p-6">
          {!uploadedDocument ? (
            <div className="space-y-4">
              <FileUploader
                onUploadSuccess={handleUploadSuccess}
                onFileDelete={handleFileDelete}
                onUploadError={handleUploadError}
              />

              {/* Upload Error Display */}
              {uploadError && (
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        {t("fileUploadModal.errors.uploadFailed")}
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                        {uploadError}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Success State
            <div className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">
                      {t("fileUploadModal.success.title")}
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      {uploadedDocument.fileName}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      handleFileDelete();
                    }}
                    className="text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium px-3 py-1 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                  >
                    {t("fileUploadModal.success.changeFile")}
                  </button>
                </div>
              </div>

              {/* File Info */}
              {uploadedDocument.downloadUrl && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {t("fileUploadModal.success.ready")}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {t("fileUploadModal.success.clickDone")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
          <div className="flex gap-2 ml-auto">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {t("fileUploadModal.footer.cancel")}
            </button>
            <button
              onClick={handleClose}
              disabled={!uploadedDocument}
              className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
            >
              {t("fileUploadModal.footer.done")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;
